import { Link, useStaticQuery, graphql } from 'gatsby';
import React from 'react';
import { isEmpty } from 'lodash';

import {
  AppBar,
  Divider,
  List,
  Stack,
  Typography,
  ListItem,
  Collapse,
  Toolbar,
  IconButton,
  Box,
  Dialog,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CollapseBackLeftIcon from '../../../../images/icon-collapse-sidebar.svg';
import CollapseBackRightIcon from '../../../../images/icon-collapse-sidebar-right.svg';

import {
  PermanentDrawer,
  PermanentDrawerList,
  MobileDrawer,
  StyledListItemButton,
  StyledListItemText,
  listItemTypography,
  listItemTypographyMobile,
  CollapseListItem,
  CollapseListItemMobile,
  CollapseListItemText,
  CollapseListItemTextMobile,
  collapseListItemTypography,
  StyledButton,
  ExpandMore,
} from './SidebarStyle';

const initialValueOfShowingComingSoon = {
  0: false,
  1: false,
  2: false,
};

const Sidebar = ({ activeItem, handleSidebarButton, sidebarOpen }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileDialog, setMobileDialog] = React.useState(false);

  const [showComingSoon, setShowComingSoon] = React.useState(initialValueOfShowingComingSoon);

  const [openBible, setOpenBible] = React.useState(false);
  const [openReformation, setOpenReformation] = React.useState(false);
  const [openMovement, setOpenMovement] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(null);

  // toggle drawer for mobile devices
  const handleMobileDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setMobileDialog(false);
  };

  // close expand people for mobile dialog box
  const handleCloseDialog = () => {
    setOpenBible(false);
    setOpenReformation(false);
    setOpenMovement(false);
    setMobileOpen(!mobileOpen);
  };

  // handle collapse sub-menu for bible
  const handleBibleClick = () => {
    setOpenBible(!openBible);
  };

  // handle collapse sub-menu for reformation
  const handleReformationClick = () => {
    setOpenReformation(!openReformation);
  };

  // handle collapse sub-menu for movement
  const handleMovementClick = () => {
    setOpenMovement(!openMovement);
  };

  // handle active state for menu
  const buttonProps = (value) => ({
    selected: selectedIndex === value,
    onClick: () => setSelectedIndex(value),
  });

  const { theBeginningData, theReformationData, theMovementData } = useStaticQuery(graphql`
    query SidebarQuery {
      theBeginningData: allPrismicPeople(
        filter: { data: { birthdate: { gt: "0001-01-01" }, date_of_death: { lt: "0330-01-01" }, ad_bc: { eq: "BC" } } }
      ) {
        nodes {
          data {
            title {
              text
            }
          }
          uid
        }
        totalCount
      }
      theReformationData: allPrismicPeople(
        filter: {
          data: {
            birthdate: { gt: "0330-01-01", lt: "1840-01-01" }
            date_of_death: { lt: "1840-01-01", gt: "0330-01-01" }
            ad_bc: { eq: "AD" }
          }
        }
      ) {
        nodes {
          data {
            title {
              text
            }
          }
          uid
        }
        totalCount
      }
      theMovementData: allPrismicPeople(
        filter: { data: { birthdate: { gt: "1840-01-01" }, date_of_death: { gt: "1840-01-01" }, ad_bc: { eq: "AD" } } }
      ) {
        nodes {
          data {
            title {
              text
            }
          }
          uid
        }
        totalCount
      }
    }
  `);

  const sidebarData = [
    {
      name: `The Bible`,
      href: theBeginningData.totalCount ? `/timeline-details/TheBeginning` : `#`,
      items: theBeginningData?.nodes,
      activeMenu: `The Beginning`,
      open: openBible,
      handleClick: handleBibleClick,
    },
    {
      name: `The Reformation`,
      href: theReformationData.totalCount ? `/timeline-details/TheReformation` : `#`,
      items: theReformationData?.nodes,
      activeMenu: `The Reformation`,
      open: openReformation,
      handleClick: handleReformationClick,
    },
    {
      name: `Adventist`,
      href: theMovementData.totalCount ? `/timeline-details/TheMovement` : `#`,
      items: theMovementData?.nodes,
      activeMenu: `The Movement`,
      open: openMovement,
      handleClick: handleMovementClick,
    },
  ];

  const desktopDrawer = (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <PermanentDrawerList component="nav" aria-labelledby="timeline-drawer" open={sidebarOpen}>
          {sidebarData.map(({ name, href, items, activeMenu, open, handleClick }, i) => (
            <React.Fragment key={i}>
              <Stack direction="row" alignItems="center">
                <Link
                  to={href}
                  onClick={!isEmpty(items) && handleClick}
                  onMouseOver={() => {
                    setShowComingSoon({ ...showComingSoon, [i]: true });
                  }}
                  onMouseOut={() => {
                    setShowComingSoon(initialValueOfShowingComingSoon);
                  }}
                >
                  <StyledListItemButton {...buttonProps(activeMenu)} selected={activeItem === activeMenu}>
                    <ListItem disableGutters>
                      <StyledListItemText primary={name} primaryTypographyProps={{ style: listItemTypography }} />
                    </ListItem>
                  </StyledListItemButton>
                </Link>
                {!isEmpty(items) && (
                  <ExpandMore onClick={handleClick} aria-expanded={open} aria-label="show more">
                    <ExpandMoreIcon />
                  </ExpandMore>
                )}
                {isEmpty(items) && showComingSoon[i] && (
                  <p
                    data-aos-duration="800"
                    data-aos-easing="ease-in-out"
                    data-aos-mirror="true"
                    data-aos-once="false"
                    data-aos={'fade-in'}
                    expand={open}
                    style={{ color: 'white', fontSize: 14 }}
                  >
                    Coming Soon
                  </p>
                )}
              </Stack>
              <Collapse in={open} timeout="auto">
                <List component="div" disablePadding>
                  {!isEmpty(items) &&
                    items.map(({ data: { title }, uid }, idx) => (
                      <CollapseListItem
                        key={idx}
                        {...buttonProps(uid)}
                        component={Link}
                        to={`/timeline-details/people/${uid}`}
                      >
                        <CollapseListItemText
                          primary={title.text}
                          primaryTypographyProps={{ style: collapseListItemTypography }}
                        />
                      </CollapseListItem>
                    ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </PermanentDrawerList>
      </Box>
      <IconButton
        sx={{
          background: '#ffffff0f',
          width: 'fit-content',
          borderRadius: 0,
          ml: 'auto',
          position: 'fixed',
          left: sidebarOpen ? '225px' : 0,
          bottom: 0,
          // [sidebarOpen ? 'right' : 'left']: 0,
          padding: '12px 12px',
        }}
        onClick={handleSidebarButton}
      >
        <img src={sidebarOpen ? CollapseBackLeftIcon : CollapseBackRightIcon} alt="collapse-sidebar" />
      </IconButton>
    </>
  );

  const mobileDrawer = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <List component="nav" aria-labelledby="timeline-drawer-mobile" sx={{ width: '100%' }}>
        {sidebarData.map(({ name, href, items, activeMenu, open, handleClick }, i) => (
          <React.Fragment key={i}>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Link to={href} onClick={handleClick}>
                <StyledListItemButton {...buttonProps(0)} selected={activeItem === activeMenu}>
                  <ListItem disableGutters>
                    <StyledListItemText primary={name} primaryTypographyProps={{ style: listItemTypographyMobile }} />
                  </ListItem>
                </StyledListItemButton>
              </Link>
              {!isEmpty(items) && <ChevronRightIcon sx={{ color: '#0A0A0A' }} onClick={handleClick} />}
            </Stack>
            <Divider />
            <Dialog
              fullScreen
              open={open}
              onClose={handleClick}
              sx={{
                zIndex: '9999999999',
                display: { xs: 'block', md: 'none' },
                ['& .MuiDialog-paper']: {
                  background: '#FFFFFF',
                  color: '#0A0A0A',
                },
              }}
            >
              <AppBar sx={{ position: 'relative', height: '81px', background: '#0A0A0A', color: '#fff' }}>
                <Toolbar sx={{ height: '100%' }}>
                  <IconButton edge="start" color="inherit" onClick={handleClick} aria-label="back">
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography
                    sx={{
                      flex: 1,
                      textTransform: 'uppercase',
                      fontSize: '22px',
                      lineHeight: '128%',
                      textAlign: 'center',
                    }}
                    variant="h6"
                    component="div"
                  >
                    {name}
                  </Typography>
                  <IconButton edge="start" color="inherit" onClick={handleCloseDialog} aria-label="close">
                    <CloseIcon />
                  </IconButton>
                </Toolbar>
              </AppBar>
              <List component="div" disablePadding>
                {!isEmpty(items) &&
                  items.map(({ data: { title }, uid }, idx) => (
                    <CollapseListItemMobile
                      key={idx}
                      {...buttonProps(`0.${idx}`)}
                      component={Link}
                      to={`/timeline-details/people/${uid}`}
                    >
                      <CollapseListItemTextMobile
                        primary={title.text}
                        primaryTypographyProps={{ style: collapseListItemTypography }}
                      />
                    </CollapseListItemMobile>
                  ))}
              </List>
            </Dialog>
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Button drawer for mobile devices */}
      <Box
        sx={{
          textAlign: 'center',
          width: '100%',
          display: { xs: 'block', md: 'none' },
          // padding: '0 5vw',
          // marginTop: '17px',
        }}
      >
        <StyledButton
          variant="contained"
          sx={{ width: '100%', pt: '12px', pb: '8px' }}
          onClick={handleMobileDrawerToggle}
        >
          {activeItem ?? `Stages`}
        </StyledButton>
      </Box>
      <MobileDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{ display: { xs: 'block', md: 'none' } }}
      >
        <AppBar sx={{ position: 'relative', height: '81px', background: '#0A0A0A', color: '#fff' }}>
          <Toolbar sx={{ height: '100%' }}>
            <Typography
              sx={{ flex: 1, textTransform: 'uppercase', fontSize: '22px', lineHeight: '128%', textAlign: 'center' }}
              variant="h6"
              component="div"
            >
              Stages
            </Typography>
            <IconButton edge="start" color="inherit" onClick={handleMobileDrawerToggle} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        {mobileDrawer}
      </MobileDrawer>

      {/* Permanent drawer for desktop */}
      <PermanentDrawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' } }} open={sidebarOpen}>
        {desktopDrawer}
      </PermanentDrawer>
    </>
  );
};

export default Sidebar;
